import type { ItemSlot } from './types';

export interface ItemGridOptions {
  /** Unique id used by GridDragDrop to identify this grid. */
  gridId: string;
  label: string;
  /** Total number of slots to render (filled + empty). */
  capacity: number;
  slots: ItemSlot[];
  /** Accent colour for the label. Defaults to #93c5fd. */
  labelColor?: string;
  /** Inner CSS grid gap in px. Defaults to 4. */
  gridGapPx?: number;
  /** Allow the slot area to scroll inside a constrained parent. */
  scrollable?: boolean;
}

const SLOT_SIZE = 56; // px — square slot dimensions
const DRAG_TYPE = 'application/item-grid-slot';

export interface SlotDragData {
  gridId: string;
  slotId: string;
}

export class ItemGrid {
  public readonly element: HTMLDivElement;
  private readonly gridEl: HTMLDivElement;
  public readonly gridId: string;

  // Callbacks set by GridDragDrop or consumer
  public onDragStart?: (slotId: string) => void;
  public onDrop?: (targetSlotId: string, data: SlotDragData) => void;
  public onSlotClick?: (slotId: string) => void;
  public onSlotContextMenu?: (slotId: string, event: MouseEvent) => void;
  public onSlotMouseEnter?: (slotId: string, event: MouseEvent) => void;
  public onSlotMouseLeave?: (slotId: string, event: MouseEvent) => void;
  public onSlotMouseMove?: (slotId: string, event: MouseEvent) => void;

  constructor(opts: ItemGridOptions) {
    this.gridId = opts.gridId;

    this.element = document.createElement('div');
    Object.assign(this.element.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: '0',
      minHeight: '0',
    });

    const labelEl = document.createElement('div');
    labelEl.textContent = opts.label;
    Object.assign(labelEl.style, {
      color: opts.labelColor ?? '#93c5fd',
      fontSize: '0.78rem',
      fontWeight: 'bold',
      marginBottom: '2px',
    });
    this.element.appendChild(labelEl);

    this.gridEl = document.createElement('div');
    const gridGapPx = Number.isFinite(opts.gridGapPx) ? Math.max(0, Math.floor(opts.gridGapPx)) : 4;
    Object.assign(this.gridEl.style, {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${SLOT_SIZE}px, 1fr))`,
      gap: `${gridGapPx}px`,
      minHeight: '0',
    });
    if (opts.scrollable) {
      this.element.style.overflow = 'hidden';
      this.gridEl.style.flex = '1';
      this.gridEl.style.alignContent = 'start';
      this.gridEl.style.overflowY = 'auto';
      this.gridEl.style.paddingRight = '4px';
    }
    this.element.appendChild(this.gridEl);

    this.render(opts.slots, opts.capacity);
  }

  public update(slots: ItemSlot[], capacity: number): void {
    this.gridEl.innerHTML = '';
    this.render(slots, capacity);
  }

  public destroy(): void {
    this.element.remove();
  }

  private render(slots: ItemSlot[], capacity: number): void {
    // Build a map for O(1) lookup
    const byId = new Map(slots.map((s) => [s.id, s]));

    // We render exactly `capacity` slots in index order.
    // Slots that have an item fill their position; the rest are empty.
    // For grids where slot ids are numeric indices, we iterate 0..capacity-1.
    // For grids where ids are arbitrary (e.g. inventoryId), we render filled
    // slots first then pad with empty slots up to capacity.
    const ordered: ItemSlot[] = [];
    const numericIds = slots.every((s) => /^\d+$/.test(s.id));

    if (numericIds) {
      for (let i = 0; i < capacity; i++) {
        const slot = byId.get(String(i));
        ordered.push(slot ?? { id: String(i), item: null });
      }
    } else {
      for (const s of slots) ordered.push(s);
      while (ordered.length < capacity) {
        ordered.push({ id: `__empty_${ordered.length}`, item: null });
      }
    }

    for (const slot of ordered) {
      this.gridEl.appendChild(this.buildSlotEl(slot));
    }
  }

  private buildSlotEl(slot: ItemSlot): HTMLDivElement {
    const el = document.createElement('div');
    if (slot.className) el.className = slot.className;

    Object.assign(el.style, {
      width: `${SLOT_SIZE}px`,
      height: `${SLOT_SIZE}px`,
      backgroundColor: '#1e2535',
      border: '1px solid #2a3142',
      borderRadius: '4px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.65rem',
      color: '#cbd5e1',
      position: 'relative',
      userSelect: 'none',
      boxSizing: 'border-box',
    });

    const installDropHandlers = (): void => {
      el.addEventListener('dragover', (e) => {
        if (e.dataTransfer?.types.includes(DRAG_TYPE)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          el.style.borderColor = '#60a5fa';
          el.style.backgroundColor = '#1e3a5f';
        }
      });

      el.addEventListener('dragleave', () => {
        el.style.borderColor = '#2a3142';
        el.style.backgroundColor = '#1e2535';
      });

      el.addEventListener('drop', (e) => {
        e.preventDefault();
        el.style.borderColor = '#2a3142';
        el.style.backgroundColor = '#1e2535';
        const raw = e.dataTransfer?.getData(DRAG_TYPE);
        if (!raw) return;
        const data: SlotDragData = JSON.parse(raw) as SlotDragData;
        this.onDrop?.(slot.id, data);
      });
    };

    if (slot.item) {
      el.draggable = true;
      el.style.cursor = 'grab';

      const emojiEl = document.createElement('div');
      emojiEl.textContent = slot.item.emoji;
      emojiEl.style.fontSize = '1.4rem';
      emojiEl.style.lineHeight = '1';

      const qtyEl = document.createElement('div');
      qtyEl.textContent = slot.item.quantity > 1 ? String(slot.item.quantity) : '';
      qtyEl.style.color = '#94a3b8';
      qtyEl.style.fontSize = '0.62rem';

      el.title = `${slot.item.displayName}${slot.item.quantity > 1 ? ` ×${slot.item.quantity}` : ''}`;
      el.appendChild(emojiEl);
      el.appendChild(qtyEl);

      el.addEventListener('click', () => {
        this.onSlotClick?.(slot.id);
      });

      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.onSlotContextMenu?.(slot.id, e);
      });

      el.addEventListener('mouseenter', (e) => {
        this.onSlotMouseEnter?.(slot.id, e);
      });

      el.addEventListener('mousemove', (e) => {
        this.onSlotMouseMove?.(slot.id, e);
      });

      el.addEventListener('mouseleave', (e) => {
        this.onSlotMouseLeave?.(slot.id, e);
      });

      el.addEventListener('dragstart', (e) => {
        const data: SlotDragData = { gridId: this.gridId, slotId: slot.id };
        e.dataTransfer!.setData(DRAG_TYPE, JSON.stringify(data));
        e.dataTransfer!.setData('application/inventory-id', slot.dragDataId ?? slot.id);
        e.dataTransfer!.effectAllowed = 'move';
        el.style.opacity = '0.4';
        this.onDragStart?.(slot.id);
      });

      el.addEventListener('dragend', () => {
        el.style.opacity = '1';
      });

      installDropHandlers();
    } else {
      // Empty slot
      el.style.cursor = 'default';
      el.style.opacity = '0.4';
      installDropHandlers();
    }

    return el;
  }
}
